// #4942 / #4994 follow-up (df-6bc15f1a946e): decide whether the app has been asking the
// server to log it in far too often, and describe the burst when it has.
//
// `attemptCouchLoginAsync` deduplicates logins that OVERLAP in time (callers share one
// in-flight promise) and does nothing about logins that FOLLOW one another. Twice in
// production one device produced a stream of them — 235 calls in nine minutes (#4942) and
// 432 sign-ins in 91 seconds (#4994). Every call succeeded, so nothing errored, no watchdog
// fired, and nothing named the caller. Both storms were found days later in server logs
// because somebody went looking.
//
// TWO THINGS DECIDE THE SHAPE OF THIS MODULE, and both come from what those storms actually
// were: page-RELOAD loops. The web view kept reloading its own page — about 2.6 times a
// second in #4942 — and the server saw exactly one login per page load.
//
//  1. The record of recent starts has to SURVIVE A RELOAD, so the caller persists it (see
//     couch-login-storm-store.ts). An in-memory counter would have reset on every reload
//     and counted 1 every time.
//  2. The decision looks for the densest window ANYWHERE in the record, not for a window
//     ending now. A report filed inside a 370 ms page life may never leave the device, so a
//     later, calm login has to be able to look back, find the burst nobody reported, and
//     report it then. Anchoring the window to "now" would make that impossible.
//
// This module is deliberately pure and node-loadable, like couch-login-online-gate.ts and
// couch-login-retry-gate.ts: auth.service.ts holds the state and the side effects.
//
// NOTHING HERE EVER REFUSES OR SKIPS A LOGIN, and that is a decision rather than an
// omission. A bound cannot fix a reboot loop — each reboot legitimately needs one login —
// so refusing them would leave the user with an app that both reboots AND cannot sync.
// design-docs/telemetry.md records the full reasoning under "Couch login storms".

/**
 * Why a caller is asking for a couch login. One value per call site, so a storm report can
 * say which part of the app drove it — the question that took days to answer in #4942.
 */
export enum CouchLoginReason {
  /** The cold-boot login on the critical path (onLoginStateChange). */
  BootLogin = 'BootLogin',
  /** The user tapped the splash screen's Retry button. */
  SplashRetryManual = 'SplashRetryManual',
  /** An automatic retry after the window regained focus or the device came back online. */
  DeferredRetryAuto = 'DeferredRetryAuto',
  /** A database request was rejected, so the fetch wrapper is refreshing the session. */
  FetchRelogin = 'FetchRelogin',
  /** Warm resume: the cached role says locked out, so re-check the server before showing it. */
  WarmResumeLockoutRecheck = 'WarmResumeLockoutRecheck',
  /** Warm resume phase B: the deferred login, after the splash is gone. */
  WarmResumePhaseB = 'WarmResumePhaseB',
  /** A redeem code was accepted; re-read the role it granted. */
  RedeemCode = 'RedeemCode',
  /** The profile page is refreshing a licence that may have expired in the background. */
  ProfileLicenseRefresh = 'ProfileLicenseRefresh',
  /** A coach-invite claim was applied; read the role the server just granted. */
  CoachClaimAfterApply = 'CoachClaimAfterApply',
  /** The claim's short pre-reload retry loop, waiting for that role to propagate. */
  CoachClaimRolePropagation = 'CoachClaimRolePropagation',
  /** The post-reload claim poll, waiting for a role that had not landed before the reload. */
  CoachClaimUnstickPoll = 'CoachClaimUnstickPoll',
  /** A coach changed tier through the store; read the new grant. */
  CoachTierChange = 'CoachTierChange',
  /** A coach tier purchase was confirmed; read the new grant. */
  CoachTierConfirm = 'CoachTierConfirm',
  /** A coach-tier purchase our server never heard about was re-sent and accepted;
   *  read the grant it should have made at the time. Its own reason rather than
   *  `CoachTierConfirm`, so a run of these in the storm log reads as a recovery
   *  rather than as coaches buying plans. */
  CoachTierPendingRetry = 'CoachTierPendingRetry',
  /** An E2E test asked for a session refresh through the test hook. */
  E2eTestHook = 'E2eTestHook',
  /** Polling for the paid role after a PayPro checkout, while its webhook lands. */
  PayProPaidPoll = 'PayProPaidPoll',
  /** One more read after the PayPro "still processing" alert was dismissed. */
  PayProAfterAlert = 'PayProAfterAlert',
  /** A store purchase finished; read the subscription it granted. */
  StorePurchaseFinish = 'StorePurchaseFinish',
  /** A coach seat purchase finished; read the Premium grant and tier cap it added. */
  CoachSeatPurchaseFinish = 'CoachSeatPurchaseFinish',
  /** The user restored their store purchases; read what that restored. */
  StoreRestore = 'StoreRestore',
  /** The device went from offline to online. */
  OnlineRegained = 'OnlineRegained',
  /** The server re-validated the subscription; read any status change it made. */
  SubscriptionRevalidated = 'SubscriptionRevalidated',
  /** The user switched plan; refresh the entitlements without waiting for the next tick. */
  SubscriptionPlanSwitch = 'SubscriptionPlanSwitch',
}

/** One recorded couch-login start. */
export interface CouchLoginStart {
  /** Wall-clock time the login started, in milliseconds since the epoch. */
  atMs: number;
  /** Which caller asked for it. */
  reason: CouchLoginReason;
  /**
   * Identifies the JavaScript context that recorded this start — a fresh random value per
   * page load. Counting how many DISTINCT ones a burst spans is what separates "one page is
   * looping" from "the page keeps reloading", and those two need fixes at different layers.
   */
  contextId: string;
}

/** How wide a window the storm rule looks at. */
export const STORM_WINDOW_MS = 10_000;

/**
 * How many starts inside that window make a storm. A start count of exactly this number is
 * NOT a storm; it has to be exceeded.
 *
 * Margin on both sides, which is the whole reason for the value. The worst LEGITIMATE burst
 * in the app is a coach-invite claim: up to 5 logins in ~3.7 s while the granted role
 * propagates (coach-claim.service.ts, delays [200, 500, 1000, 2000] ms), then a reload, then
 * the boot login, then a poll at one login per 3 s — about 7 inside a 10-second window. The
 * two measured storms ran at roughly 26-30 (#4942) and 47 (#4994) per 10 seconds.
 */
export const STORM_MIN_STARTS_IN_WINDOW = 12;

/**
 * How long to wait before reporting again, so a storm that runs for minutes files one report
 * rather than one per login. A continuing storm re-reports after this, which is deliberate:
 * a burst that outlives the cooldown is a different fact from one that stopped.
 */
export const STORM_REPORT_COOLDOWN_MS = 5 * 60_000;

/**
 * How many starts to keep. It only has to be big enough to hold one window's worth at storm
 * rates: with more than 12 starts making a storm, a full record of this size always contains
 * enough to recognise one.
 */
export const LOGIN_START_RING_CAPACITY = 24;

/** What a storm looked like, for the report. */
export interface CouchLoginStormSummary {
  /** How many login starts fell inside the densest window. */
  startsInWindow: number;
  /** How wide that window is (STORM_WINDOW_MS), so a reader need not know the constant. */
  windowMs: number;
  /** Milliseconds between the first and last start in that window. */
  spanMs: number;
  /** How many separate page loads those starts came from. More than 1 means a reload loop. */
  distinctContexts: number;
  /** How many starts each caller contributed, worst first. */
  reasonCounts: { reason: CouchLoginReason; count: number }[];
  /** The newest start in the window — what a later run compares against, so one burst reports once. */
  newestStartAtMs: number;
}

/** The persisted state this module reasons over. */
export interface CouchLoginStormState {
  /** Recent login starts, oldest first, at most LOGIN_START_RING_CAPACITY of them. */
  starts: CouchLoginStart[];
  /** When the last storm report was filed, or null if none ever was. */
  lastReportedAtMs: number | null;
  /**
   * The newest start already covered by a filed report, or null. Starts at or before this
   * are never reported again, so one burst produces one report even after the cooldown ends.
   */
  reportedThroughMs: number | null;
}

/** The state a device with no record yet starts from. */
export function emptyCouchLoginStormState(): CouchLoginStormState {
  return { starts: [], lastReportedAtMs: null, reportedThroughMs: null };
}

/**
 * Add one start to the record, keeping it oldest-first and no longer than the capacity.
 * Pure: it returns a new list and does not touch the one it was given.
 */
export function appendLoginStart(
  starts: CouchLoginStart[],
  start: CouchLoginStart,
): CouchLoginStart[] {
  const appended = [...starts, start];
  // if the record has outgrown the capacity, drop from the oldest end
  if (appended.length <= LOGIN_START_RING_CAPACITY) return appended;
  return appended.slice(appended.length - LOGIN_START_RING_CAPACITY);
}

/**
 * Find the densest window in the record and describe it, or return null when no window holds
 * more than STORM_MIN_STARTS_IN_WINDOW starts.
 *
 * It searches every window rather than only the one ending now, which is what lets a calm
 * login report a burst that happened before the last reload. Starts at or before
 * `state.reportedThroughMs` are ignored, so a burst that has already been reported does not
 * report again when the cooldown ends.
 *
 * ONE STORM CAN STILL GO UNREPORTED, and the trade is deliberate. The caller marks a burst
 * reported BEFORE it sends the report, because a send that navigates the page away would
 * otherwise file one report per reload — hundreds in a reload storm. So a burst that ends soon
 * after crossing the threshold, whose send is lost to that same reload, is excluded from here
 * on and no later login can re-file it. The two requirements genuinely conflict: suppressing an
 * unconfirmed send is exactly what stops the flood. A storm that keeps running re-reports after
 * the cooldown, so what is lost is the short burst that stops right after it is first seen.
 */
export function summariseCouchLoginStorm(state: CouchLoginStormState): CouchLoginStormSummary | null {
  const fresh = state.starts
    .filter(s => state.reportedThroughMs == null || s.atMs > state.reportedThroughMs)
    .sort((a, b) => a.atMs - b.atMs);
  let densest: CouchLoginStart[] = [];
  // for each start, take it as the window's opening edge and count what falls inside
  for (let i = 0; i < fresh.length; i++) {
    const windowEndMs = fresh[i].atMs + STORM_WINDOW_MS;
    const inWindow: CouchLoginStart[] = [];
    for (let j = i; j < fresh.length && fresh[j].atMs <= windowEndMs; j++) {
      inWindow.push(fresh[j]);
    }
    if (inWindow.length > densest.length) densest = inWindow;
  }
  if (densest.length <= STORM_MIN_STARTS_IN_WINDOW) return null;

  const counts = new Map<CouchLoginReason, number>();
  for (const start of densest) {
    counts.set(start.reason, (counts.get(start.reason) ?? 0) + 1);
  }
  return {
    startsInWindow: densest.length,
    windowMs: STORM_WINDOW_MS,
    spanMs: densest[densest.length - 1].atMs - densest[0].atMs,
    distinctContexts: new Set(densest.map(s => s.contextId)).size,
    reasonCounts: [...counts.entries()]
      .map(([reason, count]) => ({ reason, count }))
      .sort((a, b) => b.count - a.count || a.reason.localeCompare(b.reason)),
    newestStartAtMs: densest[densest.length - 1].atMs,
  };
}

/**
 * The whole decision: given the record and the current time, should a storm be reported, and
 * what did it look like? Returns null when there is nothing to report.
 */
export function resolveCouchLoginStormReport(
  state: CouchLoginStormState,
  nowMs: number,
): CouchLoginStormSummary | null {
  // if a report was filed recently, stay quiet — a storm running for minutes files one report
  if (state.lastReportedAtMs != null && nowMs - state.lastReportedAtMs < STORM_REPORT_COOLDOWN_MS) {
    return null;
  }
  return summariseCouchLoginStorm(state);
}
